import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, CheckCircle2, EyeOff, PlayCircle, Plus, Users2 } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

export default function TrainingContentAdmin() {
  const [trainings, setTrainings] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [newForm, setNewForm] = useState({
    title: "",
    description: "",
    category: "",
    duration_minutes: 10,
    required: false,
    due_date: "",
    video_source: "embed",
    video_embed_url: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/training/trainings", { params: { include_archived: showArchived } });
      setTrainings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
     
  }, [showArchived]);

  const create = async () => {
    if (!newForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...newForm,
        title: newForm.title.trim(),
        description: newForm.description.trim(),
        due_date: newForm.due_date || null,
      };
      await api.post("/training/trainings", body);
      toast.success("Training created. Add a quiz + assign an audience next.");
      setNewForm({
        title: "", description: "", category: "", duration_minutes: 10,
        required: false, due_date: "", video_source: "embed", video_embed_url: "",
      });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (t) => {
    try {
      await api.post(`/training/trainings/${t.id}/${t.published ? "unpublish" : "publish"}`);
      toast.success(t.published ? "Unpublished." : "Published.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const archive = async (t) => {
    try {
      await api.post(`/training/trainings/${t.id}/archive`);
      toast.success("Archived.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="training-admin-page">
      <div>
        <ModuleTitleTRAINING />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Manage Trainings</h1>
        <p className="mt-2 text-charcoal-soft">
          Add trainings, edit content and quizzes, publish and assign — all in one place.
        </p>
      </div>

      <section className="patria-card p-6" data-testid="training-new-form">
        <h3 className="font-serif text-forest-900 text-xl">Create A New Training</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="patria-label">Title *</label>
            <input
              className="patria-input mt-2 w-full"
              value={newForm.title}
              onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
              data-testid="training-new-title"
            />
          </div>
          <div>
            <label className="patria-label">Category</label>
            <input
              className="patria-input mt-2 w-full"
              value={newForm.category}
              onChange={(e) => setNewForm({ ...newForm, category: e.target.value })}
              data-testid="training-new-category"
            />
          </div>
          <div>
            <label className="patria-label">Duration (min)</label>
            <input
              type="number"
              min={0}
              className="patria-input mt-2 w-full"
              value={newForm.duration_minutes}
              onChange={(e) => setNewForm({ ...newForm, duration_minutes: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="patria-label">Default due date</label>
            <input
              type="date"
              className="patria-input mt-2 w-full"
              value={newForm.due_date}
              onChange={(e) => setNewForm({ ...newForm, due_date: e.target.value })}
              data-testid="training-new-due"
            />
          </div>
          <div>
            <label className="patria-label">Video source</label>
            <select
              className="patria-input mt-2 w-full"
              value={newForm.video_source}
              onChange={(e) => setNewForm({ ...newForm, video_source: e.target.value })}
              data-testid="training-new-video-source"
            >
              <option value="embed">Embedded YouTube/Vimeo</option>
              <option value="upload">Direct upload (add file after saving)</option>
            </select>
          </div>
          {newForm.video_source === "embed" && (
            <div>
              <label className="patria-label">Video URL</label>
              <input
                className="patria-input mt-2 w-full font-mono text-xs"
                value={newForm.video_embed_url}
                onChange={(e) => setNewForm({ ...newForm, video_embed_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="training-new-video-url"
              />
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="patria-label">Description</label>
            <textarea
              className="patria-input mt-2 w-full"
              rows={3}
              value={newForm.description}
              onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
              data-testid="training-new-description"
            />
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-charcoal">
            <input
              type="checkbox"
              checked={newForm.required}
              onChange={(e) => setNewForm({ ...newForm, required: e.target.checked })}
              data-testid="training-new-required"
            />
            Mark as required
          </label>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={create}
            disabled={busy}
            className="patria-btn-primary"
            data-testid="training-new-submit"
          >
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Create training
          </button>
        </div>
      </section>

      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-sm text-charcoal-soft">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="training-show-archived"
          />
          Show archived
        </label>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {trainings.length === 0 && (
          <div className="patria-card p-6 md:col-span-2">
            <p className="text-sm text-charcoal-muted italic">No trainings.</p>
          </div>
        )}
        {trainings.map((t) => (
          <div key={t.id} className="patria-card p-5" data-testid={`training-admin-row-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="font-serif text-forest-900 text-xl leading-snug">{t.title}</p>
              <div className="flex flex-col gap-1 shrink-0 text-right">
                {t.required && (
                  <span className="inline-flex items-center rounded-full border border-terracotta-500/30 bg-terracotta-500/15 text-terracotta-600 px-2 py-0.5 text-[11px]">
                    Required
                  </span>
                )}
                {t.published ? (
                  <span className="inline-flex items-center rounded-full bg-forest-600 text-cream-100 px-2 py-0.5 text-[11px]">
                    Published
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-charcoal/20 text-charcoal px-2 py-0.5 text-[11px]">
                    Draft
                  </span>
                )}
                {t.archived && (
                  <span className="inline-flex items-center rounded-full bg-charcoal/40 text-cream-100 px-2 py-0.5 text-[11px]">
                    Archived
                  </span>
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-charcoal-muted">
              {t.category || "—"} • {t.duration_minutes || 0} min
              {t.quiz?.enabled && ` • Quiz (${t.quiz.questions.length}q, ${t.quiz.passing_score_pct}% pass)`}
            </p>
            {t.description && (
              <p className="mt-2 text-sm text-charcoal-soft line-clamp-3">{t.description}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/training/admin/${t.id}`}
                className="patria-btn-secondary text-xs py-1.5"
                data-testid={`training-edit-${t.id}`}
              >
                Edit
              </Link>
              <Link
                to={`/training/admin/${t.id}/assign`}
                className="patria-btn-secondary text-xs py-1.5"
                data-testid={`training-assign-${t.id}`}
              >
                <Users2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                Assign
              </Link>
              <button
                onClick={() => togglePublish(t)}
                className="patria-btn-secondary text-xs py-1.5"
                data-testid={`training-publish-${t.id}`}
              >
                {t.published ? (
                  <>
                    <EyeOff className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                    Unpublish
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                    Publish
                  </>
                )}
              </button>
              {!t.archived && (
                <button
                  onClick={() => archive(t)}
                  className="patria-btn-secondary text-xs py-1.5"
                  data-testid={`training-archive-${t.id}`}
                >
                  <Archive className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                  Archive
                </button>
              )}
              {t.video_source === "embed" && t.video_embed_url && (
                <span className="text-[11px] text-charcoal-muted inline-flex items-center gap-1">
                  <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Embed
                </span>
              )}
              {t.video_source === "upload" && t.video_storage_key && (
                <span className="text-[11px] text-charcoal-muted inline-flex items-center gap-1">
                  <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Direct upload
                </span>
              )}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
